import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Mic, MicOff, Camera, ImagePlus, Send, X, Loader2,
  Play, Pause, Square, RotateCcw, Trash2, Radio,
  Video, Hash, AtSign, Zap, User, Swords
} from 'lucide-react';

const PROMPTS = [
  "What's on your mind or say it out loud...",
  "Drop something for the DMV...",
  "Start a battle...",
  "Share your latest track...",
  "Record a freestyle...",
  "What are you working on?",
];

export function FeedInputBar({ onPostCreated }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [promptIdx, setPromptIdx] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showVoice, setShowVoice] = useState(false);

  // Rotate prompts with fade
  useEffect(() => {
    const interval = setInterval(() => {
      setPromptIdx(i => (i + 1) % PROMPTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Listen for external voice recorder trigger (from promo cards)
  useEffect(() => {
    const handler = () => setShowVoice(true);
    window.addEventListener('openVoiceRecorder', handler);
    return () => window.removeEventListener('openVoiceRecorder', handler);
  }, []);

  if (!isAuthenticated) return null;

  return (
    <>
      <div className="mb-4" data-testid="feed-input-bar">
        <div
          className="flex items-center gap-3 bg-card border border-border rounded-2xl px-4 py-3.5 cursor-pointer
                     hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 active:scale-[0.995]"
          data-testid="feed-input-trigger"
        >
          {/* Avatar */}
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/20 shrink-0" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center ring-2 ring-primary/20 shrink-0">
              <User className="w-5 h-5 text-muted-foreground" />
            </div>
          )}

          {/* Placeholder — click opens create post */}
          <span
            onClick={() => setShowModal(true)}
            className="flex-1 text-sm text-muted-foreground truncate cursor-text"
            data-testid="feed-input-placeholder"
          >
            {PROMPTS[promptIdx]}
          </span>

          {/* Mic button — PRIMARY */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowVoice(true); }}
            className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center 
              hover:bg-red-500/20 hover:scale-105 transition-all active:scale-95"
            data-testid="voice-post-btn"
            title="Voice Post"
          >
            <Mic className="w-5 h-5 text-red-400" />
          </button>
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-2 mt-2 px-1" data-testid="quick-actions">
          <button
            onClick={() => setShowVoice(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors"
            data-testid="quick-record"
          >
            <Mic className="w-3.5 h-3.5" /> Record
          </button>
          <button
            onClick={() => navigate('/go-live')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-medium hover:bg-orange-500/20 transition-colors"
            data-testid="quick-go-live"
          >
            <Radio className="w-3.5 h-3.5" /> Go Live
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-xs font-medium hover:bg-violet-500/20 transition-colors"
            data-testid="quick-upload-clip"
          >
            <Camera className="w-3.5 h-3.5" /> Upload Clip
          </button>
        </div>
      </div>

      <CreatePostModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onPostCreated={onPostCreated}
      />
      <VoiceRecordModal
        open={showVoice}
        onClose={() => setShowVoice(false)}
        onPostCreated={onPostCreated}
      />
    </>
  );
}


function CreatePostModal({ open, onClose, onPostCreated }) {
  const { user, getAuthHeaders } = useAuth();
  const textRef = useRef(null);
  const fileRef = useRef(null);
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (open && textRef.current) {
      setTimeout(() => textRef.current?.focus(), 200);
    }
  }, [open]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newPreviews = files.map(f => ({
      url: URL.createObjectURL(f),
      type: f.type.startsWith('video') ? 'video' : 'image',
      name: f.name,
    }));
    setMediaFiles(prev => [...prev, ...files]);
    setMediaPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeMedia = (idx) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== idx));
    setMediaPreviews(prev => {
      URL.revokeObjectURL(prev[idx]?.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handlePost = async () => {
    if (!content.trim() && mediaFiles.length === 0) {
      toast.error('Add some content or media');
      return;
    }
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('content', content);
      mediaFiles.forEach(f => formData.append('media', f));
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API}/posts/create`, {
        method: 'POST', headers, body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Posted!');
        setContent('');
        setMediaFiles([]);
        setMediaPreviews([]);
        onClose();
        onPostCreated?.(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to post');
      }
    } catch {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg p-0 bg-card border-border overflow-hidden" data-testid="create-post-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-bold">Create Post</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3 px-4 pt-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <User className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
          <div>
            <p className="font-semibold text-sm">{user?.name || 'You'}</p>
            <p className="text-xs text-muted-foreground">@{user?.handle || 'user'}</p>
          </div>
        </div>
        <div className="px-4 py-3">
          <textarea
            ref={textRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            className="w-full bg-transparent text-foreground placeholder:text-muted-foreground resize-none outline-none text-base min-h-[100px]"
            maxLength={2000}
            data-testid="post-text-input"
          />
        </div>
        {mediaPreviews.length > 0 && (
          <div className="px-4 flex gap-2 flex-wrap">
            {mediaPreviews.map((m, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                {m.type === 'video' ? (
                  <video src={m.url} className="w-full h-full object-cover" />
                ) : (
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                )}
                <button onClick={() => removeMedia(i)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border mt-2">
          <div className="flex items-center gap-1">
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={handleFileSelect} className="hidden" />
            <button onClick={() => fileRef.current?.click()}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-blue-500/10 transition-colors" title="Add Media">
              <ImagePlus className="w-4.5 h-4.5 text-blue-400" />
            </button>
            <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-amber-500/10 transition-colors" title="Add Hashtag">
              <Hash className="w-4.5 h-4.5 text-amber-400" />
            </button>
            <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-violet-500/10 transition-colors" title="Tag Someone">
              <AtSign className="w-4.5 h-4.5 text-violet-400" />
            </button>
          </div>
          <Button
            onClick={handlePost}
            disabled={posting || (!content.trim() && mediaFiles.length === 0)}
            className="bg-primary hover:bg-primary/90 h-9 px-6 font-bold"
            data-testid="submit-post-btn"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Post</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function VoiceRecordModal({ open, onClose, onPostCreated }) {
  const { user, getAuthHeaders } = useAuth();
  const [phase, setPhase] = useState('idle'); // idle, recording, paused, preview, posting
  const [timer, setTimer] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [editTranscript, setEditTranscript] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [voiceData, setVoiceData] = useState(null);
  const [caption, setCaption] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [waveformData, setWaveformData] = useState([]);
  const [isBattle, setIsBattle] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioElRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playProgress, setPlayProgress] = useState(0);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      stopRecording(true);
      setPhase('idle');
      setTimer(0);
      setTranscript('');
      setAudioUrl(null);
      setAudioBlob(null);
      setVoiceData(null);
      setCaption('');
      setWaveformData([]);
      setIsBattle(false);
      setPlayProgress(0);
      setIsPlaying(false);
    }
  }, [open]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;

      const drawWaveform = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const bars = Array.from(dataArray.slice(0, 40)).map(v => v / 255);
        setWaveformData(bars);
        animFrameRef.current = requestAnimationFrame(drawWaveform);
      };
      drawWaveform();

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        setPhase('preview');
        uploadAndTranscribe(blob);
      };

      recorder.start(200);
      mediaRecorderRef.current = recorder;
      setPhase('recording');
      setTimer(0);
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } catch {
      toast.error('Microphone access denied. Please allow mic access.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setPhase('paused');
      clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setPhase('recording');
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
      const drawWaveform = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        setWaveformData(Array.from(dataArray.slice(0, 40)).map(v => v / 255));
        animFrameRef.current = requestAnimationFrame(drawWaveform);
      };
      drawWaveform();
    }
  };

  const stopRecording = (cleanup = false) => {
    clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (cleanup) {
      mediaRecorderRef.current = null;
      analyserRef.current = null;
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    }
  };

  const uploadAndTranscribe = async (blob) => {
    setTranscribing(true);
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      formData.append('auto_transcribe', 'true');
      const res = await fetch(`${API}/voice-posts/upload`, {
        method: 'POST', headers, body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setVoiceData(data);
        setTranscript(data.transcript || '');
        if (data.transcript) setCaption(data.transcript);
      } else {
        toast.error('Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setTranscribing(false);
    }
  };

  const togglePlayback = () => {
    if (!audioUrl) return;
    if (isPlaying && audioElRef.current) {
      audioElRef.current.pause();
      setIsPlaying(false);
    } else {
      const audio = new Audio(audioUrl);
      audio.ontimeupdate = () => {
        if (audio.duration) setPlayProgress(audio.currentTime / audio.duration);
      };
      audio.onended = () => { setIsPlaying(false); setPlayProgress(0); };
      audio.play();
      audioElRef.current = audio;
      setIsPlaying(true);
    }
  };

  const reRecord = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current = null; }
    setAudioUrl(null);
    setAudioBlob(null);
    setVoiceData(null);
    setTranscript('');
    setCaption('');
    setPhase('idle');
    setTimer(0);
    setPlayProgress(0);
    setIsPlaying(false);
  };

  const handlePost = async () => {
    if (!voiceData?.audio_url) {
      toast.error('No recording to post');
      return;
    }
    setPosting(true);
    try {
      const res = await fetch(`${API}/posts/create`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: caption || transcript || '',
          voice_post_data: {
            audio_url: voiceData.audio_url,
            transcript: transcript,
            duration: voiceData.duration || timer,
            voice_id: voiceData.voice_id,
          },
          is_battle: isBattle,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isBattle ? 'Battle post dropped!' : 'Voice post shared!');
        onClose();
        onPostCreated?.(data);
      } else {
        toast.error('Failed to post');
      }
    } catch {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  // Generate static waveform bars for preview
  const previewBars = useRef(Array(50).fill(0).map(() => 0.15 + Math.random() * 0.7));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md p-0 bg-card border-border overflow-hidden" data-testid="voice-record-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-bold flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${phase === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-muted-foreground/30'}`} />
            Voice Post
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6">
          {/* ─── IDLE ─── */}
          {phase === 'idle' && (
            <div className="text-center py-6">
              {/* Outer ring animation */}
              <div className="relative w-28 h-28 mx-auto mb-5">
                <div className="absolute inset-0 rounded-full bg-red-500/10 animate-ping" style={{ animationDuration: '2s' }} />
                <div className="absolute inset-2 rounded-full bg-red-500/5" />
                <button
                  onClick={startRecording}
                  className="absolute inset-0 w-full h-full rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center
                             shadow-xl shadow-red-500/30 hover:shadow-2xl hover:shadow-red-500/40 hover:scale-105 transition-all active:scale-95 z-10"
                  data-testid="start-record-btn"
                >
                  <Mic className="w-12 h-12 text-white" />
                </button>
              </div>
              <p className="text-sm font-medium text-foreground">Tap to start recording</p>
              <p className="text-xs text-muted-foreground mt-1">Max 5 minutes. Your voice, your way.</p>
            </div>
          )}

          {/* ─── RECORDING / PAUSED ─── */}
          {(phase === 'recording' || phase === 'paused') && (
            <div className="text-center py-4">
              {/* Waveform visualization */}
              <div className="flex items-center justify-center gap-[3px] h-20 mb-5 px-4" data-testid="waveform">
                {(waveformData.length > 0 ? waveformData : Array(40).fill(0.05)).map((v, i) => (
                  <div key={i}
                    className={`w-[5px] rounded-full transition-all ${
                      phase === 'recording'
                        ? 'bg-gradient-to-t from-red-600 to-red-400'
                        : 'bg-muted-foreground/20'
                    }`}
                    style={{
                      height: `${Math.max(6, v * 80)}px`,
                      transition: 'height 80ms ease-out',
                    }}
                  />
                ))}
              </div>

              {/* Timer */}
              <p className="text-4xl font-mono font-bold text-foreground mb-2 tracking-wider" data-testid="record-timer">
                {formatTime(timer)}
              </p>
              <Badge className={`${phase === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-amber-500'} text-white border-0 text-xs`}>
                {phase === 'recording' ? 'Recording' : 'Paused'}
              </Badge>

              {/* Controls */}
              <div className="flex items-center justify-center gap-5 mt-7">
                {phase === 'recording' ? (
                  <button onClick={pauseRecording}
                    className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center hover:bg-amber-500/25 transition-all active:scale-90"
                    data-testid="pause-record-btn">
                    <Pause className="w-6 h-6 text-amber-400" />
                  </button>
                ) : (
                  <button onClick={resumeRecording}
                    className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center hover:bg-emerald-500/25 transition-all active:scale-90"
                    data-testid="resume-record-btn">
                    <Play className="w-6 h-6 text-emerald-400" />
                  </button>
                )}
                <button onClick={() => stopRecording()}
                  className="w-18 h-18 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30 hover:bg-red-600 transition-all active:scale-90"
                  style={{ width: '72px', height: '72px' }}
                  data-testid="stop-record-btn">
                  <Square className="w-7 h-7 text-white" />
                </button>
              </div>
            </div>
          )}

          {/* ─── PREVIEW ─── */}
          {phase === 'preview' && (
            <div className="space-y-4">
              {/* Audio playback with waveform scrub */}
              <div className="bg-muted/20 rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3">
                  <button onClick={togglePlayback}
                    className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shrink-0 hover:bg-primary/90 transition-all active:scale-90 shadow-md shadow-primary/20"
                    data-testid="preview-play-btn">
                    {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    {/* Waveform with progress overlay */}
                    <div className="relative h-10 flex items-center gap-[2px]">
                      {previewBars.current.map((h, i) => {
                        const filled = (i / previewBars.current.length) <= playProgress;
                        return (
                          <div key={i}
                            className={`w-[3px] rounded-full transition-colors duration-150 ${filled ? 'bg-primary' : 'bg-muted-foreground/15'}`}
                            style={{ height: `${h * 40}px` }}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <span className="text-sm font-mono text-muted-foreground shrink-0">{formatTime(timer)}</span>
                </div>
              </div>

              {/* Transcription */}
              {transcribing ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3 px-1">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span>AI is transcribing your voice...</span>
                </div>
              ) : transcript ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Transcript</p>
                    <button onClick={() => setEditTranscript(!editTranscript)}
                      className="text-xs text-primary hover:underline font-medium" data-testid="edit-transcript-btn">
                      {editTranscript ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editTranscript ? (
                    <textarea value={transcript} onChange={(e) => { setTranscript(e.target.value); setCaption(e.target.value); }}
                      className="w-full bg-muted/20 border border-border rounded-xl p-3 text-sm text-foreground resize-none outline-none focus:border-primary/30 min-h-[80px] transition-colors"
                      data-testid="transcript-edit" />
                  ) : (
                    <p className="text-sm text-foreground bg-muted/10 rounded-xl p-3 border border-border/50 leading-relaxed"
                      data-testid="transcript-text">{transcript}</p>
                  )}
                </div>
              ) : null}

              {/* Caption */}
              <div>
                <label className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1.5 block">Caption (optional)</label>
                <textarea value={caption} onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a caption, hashtags, or tags..."
                  className="w-full bg-muted/10 border border-border rounded-xl p-3 text-sm text-foreground resize-none outline-none focus:border-primary/30 min-h-[60px] transition-colors"
                  data-testid="voice-caption" />
              </div>

              {/* Battle toggle */}
              <div
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  isBattle
                    ? 'bg-orange-500/10 border-orange-500/30'
                    : 'bg-muted/10 border-border hover:border-border/80'
                }`}
                onClick={() => setIsBattle(!isBattle)}
                data-testid="battle-toggle"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isBattle ? 'bg-orange-500/20' : 'bg-muted/30'}`}>
                  <Swords className={`w-4.5 h-4.5 ${isBattle ? 'text-orange-400' : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${isBattle ? 'text-orange-400' : 'text-foreground'}`}>
                    Turn into Battle Post
                  </p>
                  <p className="text-xs text-muted-foreground">Others can reply with their own voice</p>
                </div>
                <div className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-colors ${
                  isBattle ? 'bg-orange-500 justify-end' : 'bg-muted justify-start'
                }`}>
                  <div className="w-5 h-5 rounded-full bg-white shadow-sm transition-transform" />
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <button onClick={reRecord}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-muted/20 border border-border text-sm text-muted-foreground hover:bg-muted/40 transition-colors"
                  data-testid="re-record-btn">
                  <RotateCcw className="w-3.5 h-3.5" /> Re-record
                </button>
                <Button onClick={handlePost} disabled={posting || !voiceData}
                  className="flex-1 bg-primary hover:bg-primary/90 font-bold h-10 rounded-xl"
                  data-testid="post-voice-btn">
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> {isBattle ? 'Drop Battle' : 'Post Voice'}</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * VoicePostPlayer — Renders a voice post in the feed.
 */
export function VoicePostPlayer({ audioUrl, transcript, duration, isBattle, className = '' }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const barsRef = useRef(Array(50).fill(0).map(() => 0.15 + Math.random() * 0.7));

  const toggle = () => {
    if (!audioUrl) return;
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      const src = audioUrl.startsWith('http') ? audioUrl : `${API.replace('/api', '')}${audioUrl}`;
      const audio = new Audio(src);
      audio.ontimeupdate = () => setProgress(audio.currentTime / (audio.duration || 1));
      audio.onended = () => { setPlaying(false); setProgress(0); };
      audio.play().catch(() => {});
      audioRef.current = audio;
      setPlaying(true);
    }
  };

  const formatTime = (s) => {
    if (!s) return '0:00';
    const sec = Math.round(s);
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
  };

  return (
    <div className={`rounded-xl p-3.5 border ${isBattle ? 'bg-orange-500/5 border-orange-500/20' : 'bg-muted/10 border-border/50'} ${className}`} data-testid="voice-post-player">
      {isBattle && (
        <div className="flex items-center gap-1.5 mb-2">
          <Swords className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-xs font-bold text-orange-400 uppercase tracking-wider">Battle Post</span>
        </div>
      )}
      <div className="flex items-center gap-3">
        <button onClick={toggle}
          className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-all active:scale-90 shadow-md ${
            isBattle
              ? 'bg-gradient-to-br from-orange-500 to-red-500 shadow-orange-500/20'
              : 'bg-primary shadow-primary/20'
          }`}
          data-testid="voice-play-btn">
          {playing ? <Pause className="w-4.5 h-4.5 text-white" /> : <Play className="w-4.5 h-4.5 text-white ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="relative h-8 flex items-center gap-[2px]">
            {barsRef.current.map((h, i) => {
              const filled = (i / barsRef.current.length) <= progress;
              return (
                <div key={i}
                  className={`w-[3px] rounded-full transition-colors duration-100 ${
                    filled
                      ? (isBattle ? 'bg-orange-400' : 'bg-primary')
                      : 'bg-muted-foreground/12'
                  }`}
                  style={{ height: `${h * 32}px` }}
                />
              );
            })}
          </div>
        </div>
        <span className="text-xs font-mono text-muted-foreground shrink-0">
          {formatTime(duration)}
        </span>
      </div>
      {transcript && (
        <p className="text-xs text-muted-foreground mt-2.5 line-clamp-3 leading-relaxed" data-testid="voice-transcript-preview">
          {transcript}
        </p>
      )}
    </div>
  );
}
